import { Box, Card, CardContent, Grid, Skeleton } from "@mui/material";

/** Skeleton for the 5 metric cards in the snapshot row */
export const SnapshotCardSkeleton = () => (
	<Card
		variant="outlined"
		sx={{
			borderRadius: 2,
			height: "100%",
			minHeight: 120,
		}}
	>
		<CardContent sx={{ p: 1.75, "&:last-child": { pb: 1.75 } }}>
			<Skeleton variant="text" width="70%" height={14} sx={{ mb: 1 }} />
			<Skeleton variant="rounded" width={48} height={28} sx={{ mb: 0.5 }} />
			<Skeleton variant="text" width="60%" height={12} sx={{ mb: 0.5 }} />
			<Skeleton variant="text" width="80%" height={12} />
		</CardContent>
	</Card>
);

/** Skeleton for the full snapshot row (5 cards) – matches Grid xs=12 sm=6 md=4 lg=2.4 */
export const SnapshotRowSkeleton = () => (
	<Grid container spacing={2} sx={{ mb: 2 }}>
		{[1, 2, 3, 4, 5].map((i) => (
			<Grid item xs={12} sm={6} md={4} lg={2.4} key={i}>
				<SnapshotCardSkeleton />
			</Grid>
		))}
	</Grid>
);

/** Skeleton for medium dashboard cards (Archive, Upcoming) - minHeight ~220 */
export const DashboardCardSkeleton = () => (
	<Card
		variant="outlined"
		sx={{
			borderRadius: 2,
			height: "100%",
			minHeight: 220,
			display: "flex",
			flexDirection: "column",
		}}
	>
		<Box
			sx={(t) => ({
				py: 1,
				px: 1.5,
				borderBottom: 1,
				borderColor: "divider",
				bgcolor: t.palette.mode === "dark" ? "action.hover" : "grey.50",
				borderRadius: "12px 12px 0 0",
			})}
		>
			<Skeleton variant="text" width="60%" height={20} />
			<Skeleton variant="text" width="40%" height={16} sx={{ mt: 0.5 }} />
		</Box>
		<CardContent sx={{ flex: 1, pt: 1.5, px: 2 }}>
			<Skeleton
				variant="rectangular"
				height={32}
				sx={{ borderRadius: 1, mb: 1 }}
			/>
			<Skeleton
				variant="rectangular"
				height={32}
				sx={{ borderRadius: 1, mb: 1 }}
			/>
			<Skeleton
				variant="rectangular"
				height={32}
				sx={{ borderRadius: 1, mb: 1 }}
			/>
			<Skeleton variant="rectangular" height={32} sx={{ borderRadius: 1 }} />
		</CardContent>
	</Card>
);

/** Skeleton for the large KPI card */
export const KPICardSkeleton = () => (
	<Card
		variant="outlined"
		sx={{
			borderRadius: 2,
			height: "100%",
			display: "flex",
			flexDirection: "column",
		}}
	>
		<Box
			sx={(t) => ({
				p: 2.25,
				borderRadius: "12px 12px 0 0",
				bgcolor: t.palette.mode === "light" ? "action.hover" : "action.hover",
				borderBottom: 1,
				borderColor: "divider",
			})}
		>
			<Box
				sx={{
					display: "flex",
					flexWrap: "wrap",
					gap: 2,
					justifyContent: "space-between",
				}}
			>
				<Box>
					<Skeleton variant="text" width={100} height={16} />
					<Skeleton variant="text" width={180} height={24} sx={{ mt: 0.5 }} />
					<Skeleton variant="text" width={220} height={16} sx={{ mt: 0.5 }} />
				</Box>
				<Box sx={{ display: "flex", gap: 1 }}>
					<Skeleton variant="rounded" width={64} height={32} />
					<Skeleton variant="rounded" width={64} height={32} />
					<Skeleton variant="rounded" width={64} height={32} />
					<Skeleton variant="rounded" width={100} height={36} />
				</Box>
			</Box>
		</Box>
		<CardContent sx={{ flex: 1, p: 2 }}>
			<Box
				sx={{
					display: "flex",
					gap: 2,
					flexDirection: { xs: "column", md: "row" },
				}}
			>
				<Box sx={{ flex: 1 }}>
					<Skeleton
						variant="rectangular"
						height={24}
						sx={{ mb: 1, borderRadius: 1 }}
					/>
					<Skeleton
						variant="rectangular"
						height={200}
						sx={{ borderRadius: 1.5 }}
					/>
				</Box>
				<Box sx={{ flex: 1 }}>
					<Skeleton
						variant="rectangular"
						height={24}
						sx={{ mb: 1, borderRadius: 1 }}
					/>
					<Skeleton
						variant="rectangular"
						height={200}
						sx={{ borderRadius: 1.5 }}
					/>
				</Box>
			</Box>
		</CardContent>
	</Card>
);

/** Skeleton for the Sessions card content – fits inside DashboardCard */
export const SessionsCardSkeleton = () => (
	<Box
		sx={{
			height: "100%",
			minHeight: 280,
			display: "flex",
			flexDirection: "column",
		}}
	>
		<Box
			sx={(t) => ({
				py: 1.5,
				px: 2,
				borderBottom: 1,
				borderColor: "divider",
				bgcolor: t.palette.mode === "dark" ? "action.hover" : "grey.50",
			})}
		>
			<Skeleton variant="text" width={140} height={22} />
			<Skeleton variant="text" width={100} height={16} sx={{ mt: 0.5 }} />
		</Box>
		<Box sx={{ flex: 1, p: 2 }}>
			{[1, 2, 3, 4, 5].map((i) => (
				<Box key={i} sx={{ mb: 1.5 }}>
					<Skeleton
						variant="rectangular"
						height={52}
						sx={{ borderRadius: 1 }}
					/>
				</Box>
			))}
		</Box>
	</Box>
);
